import React, {useContext, useEffect, useRef, useState} from 'react';
import * as echarts from 'echarts';
import {Button, ButtonGroup, Grid, Sheet, Stack, Typography} from '@mui/joy';
import { Select, Option } from '@mui/joy';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {SocketContext} from "../../contexts/SocketContext";

function AnalyticsTab() {

    const {isConnected, apiHandler } = useContext(SocketContext); // Access socket and ApiHandler from context

    const chartGenderRef = useRef(null);  // Reference to the first chart container
    const chartAgeRef = useRef(null); // Reference to the second chart container
    const chartVisitorsDistroRef = useRef(null); // Reference to the third chart container
    const chartDurationDistroRef = useRef(null); // Reference to the third chart container

    const [currentWeekStart, setCurrentWeekStart] = useState(getCurrentWeekStart());

    // Navigation functions
    function handlePreviousWeek() {
        setCurrentWeekStart((prev) => prev - 7 * 24 * 60 * 60 * 1000);
    }

    function handleNextWeek() {
        setCurrentWeekStart((prev) => prev + 7 * 24 * 60 * 60 * 1000);
    }

    // Function to calculate the current week's Monday timestamp
    function getCurrentWeekStart() {
        const now = new Date();
        const monday = new Date(Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate() - now.getUTCDay() + 1, // Move to Monday
            0, 0, 0, 0
        ));
        return monday.getTime();
    }

    function loadDAnalyticsData(startOfWeekUTC) {
        if (apiHandler) {
            console.log('Sending WebSocket request to load analytics data');

            // Convert `startOfWeek` (timestamp in ms) to Date object
            const startOfWeek = new Date(startOfWeekUTC);

            // Calculate `endOfWeek` from `startOfWeek`
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setUTCDate(startOfWeek.getUTCDate() + 6);  // Move to Sunday
            endOfWeek.setUTCHours(23, 59, 59, 999); // Set to end of the day

            const message = {
                method: 'api',
                type: 'get_week_stats',
                payload: {
                    from_ms: startOfWeek.getTime(),
                    to_ms: endOfWeek.getTime(),
                },
            };
            apiHandler.sendRequest(message, {
                on_success: (data) => {
                    console.log("Request succeeded:", data);
                    // renderCharts()
                    const analyticsData = data.analytics;
                    renderVisitors(analyticsData)
                },
                on_failure: (error) => {
                    console.error("Request failed:", error);
                },
                on_loading: () => console.log("Request is loading..."),
            });

            const msg_duration_stats = {
                method: 'api',
                type: 'get_week_duration_stats',
                payload: {
                    from_ms: startOfWeek.getTime(),
                    to_ms: endOfWeek.getTime(),
                },
            };
            apiHandler.sendRequest(msg_duration_stats, {
                on_success: (data) => {
                    console.log("Request succeeded:", data);
                    // renderCharts()
                    const analyticsData = data.analytics;
                    renderVisitorsByDuration(analyticsData)
                },
                on_failure: (error) => {
                    console.error("Request failed:", error);
                },
                on_loading: () => console.log("Request is loading..."),
            });

            const msg_gender_stats = {
                method: 'api',
                type: 'get_week_gender_stats',
                payload: {
                    from_ms: startOfWeek.getTime(),
                    to_ms: endOfWeek.getTime(),
                },
            };
            apiHandler.sendRequest(msg_gender_stats, {
                on_success: (data) => {
                    console.log("Request succeeded:", data);
                    // renderCharts()
                    const analyticsData = data.analytics;
                    renderVisitorsByGender(analyticsData)
                },
                on_failure: (error) => {
                    console.error("Request failed:", error);
                },
                on_loading: () => console.log("Request is loading..."),
            });

            const msg_age_stats = {
                method: 'api',
                type: 'get_week_age_stats',
                payload: {
                    from_ms: startOfWeek.getTime(),
                    to_ms: endOfWeek.getTime(),
                },
            };
            apiHandler.sendRequest(msg_age_stats, {
                on_success: (data) => {
                    console.log("Request succeeded:", data);
                    // renderCharts()
                    const analyticsData = data.analytics;
                    renderVisitorsByAge(analyticsData)
                },
                on_failure: (error) => {
                    console.error("Request failed:", error);
                },
                on_loading: () => console.log("Request is loading..."),
            });
        } else {
            console.log('ApiHandler is not initialized');
        }
    }

    useEffect(()=> {
        if (isConnected && apiHandler) {
            loadDAnalyticsData(currentWeekStart);
        }
    }, [isConnected, apiHandler, currentWeekStart]);



    function renderVisitorsByDuration(analyticsData) {
        const mappedData = mapSessionDataToScatter(analyticsData);

        const series = Object.entries(mappedData).map(([day, data]) => ({
            name: day,
            type: 'scatter',
            data: data,
            // symbolSize: 6, // Fixed dot size for consistency
            emphasis: {
                focus: 'series'
            }
        }));

        const option = {
            tooltip: {
                trigger: 'item',
                formatter: function (params) {
                    const timeOfDay = `${Math.floor(params.value[0] / 60)}:${String(params.value[0] % 60).padStart(2, '0')}`;
                    return `<b>${params.seriesName}</b><br>Time: ${timeOfDay}<br>Duration: ${params.value[1]} mins`;
                }
            },
            grid: {
                left: '5%',
                right: '5%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                name: 'Time of Day',
                min: 0,
                max: 1440,  // 24 * 60
                axisLabel: {
                    formatter: (value) => {
                        const hour = Math.floor(value / 60);
                        return `${hour}:00`;
                    }
                },
                splitLine: {
                    show: false
                }
            },
            yAxis: {
                type: 'value',
                name: 'Duration (mins)',
                axisLabel: {
                    formatter: '{value} mins'
                },
                splitLine: {
                    show: false
                }
            },
            legend: {
                data: Object.keys(mappedData),
                left: 'center',
                bottom: 10
            },
            series
        };

        const chartInstance = echarts.getInstanceByDom(chartDurationDistroRef.current);
        if (chartInstance) chartInstance.dispose();
        const myChart2 = echarts.init(chartDurationDistroRef.current);
        myChart2.setOption(option);

        return () => myChart2.dispose();
    }


    function mapSessionDataToScatter(analyticsData) {
        const dayMapping = {
            "mon": "Monday",
            "tue": "Tuesday",
            "wed": "Wednesday",
            "thu": "Thursday",
            "fri": "Friday",
            "sat": "Saturday",
            "sun": "Sunday"
        };

        const mappedData = {};

        // Iterate through the dataset to build groups for each weekday
        Object.entries(analyticsData.days).forEach(([dayKey, dayData]) => {
            const dayName = dayMapping[dayKey];
            mappedData[dayName] = dayData.sessions.map(([duration, time]) => [time, duration]);
        });

        return mappedData;
    }


    function mapAnalyticsDataToChartData(analyticsData) {
        const dayMapping = {
            'mon': 6,
            'tue': 5,
            'wed': 4,
            'thu': 3,
            'fri': 2,
            'sat': 1,
            'sun': 0,
        };

        const chartData = [];

        Object.entries(analyticsData.days).forEach(([dayKey, dayData]) => {
            const dayIndex = dayMapping[dayKey];
            dayData.hours.forEach((value, hourIndex) => {
                chartData.push([hourIndex, dayIndex, value || '-']);
            });
        });

        return chartData;
    }

    function renderVisitors(analyticsData) {
        const mappedData = mapAnalyticsDataToChartData(analyticsData);

        const hours = [
            '12a', '1a', '2a', '3a', '4a', '5a', '6a',
            '7a', '8a', '9a', '10a', '11a',
            '12p', '1p', '2p', '3p', '4p', '5p',
            '6p', '7p', '8p', '9p', '10p', '11p'
        ];

        const days = [
            'Sunday', 'Saturday', 'Friday', 'Thursday',
            'Wednesday', 'Tuesday', 'Monday'
        ];

        const option3 = {
            tooltip: {
                position: 'top'
            },
            grid: {
                height: '50%',
                top: '10%'
            },
            xAxis: {
                type: 'category',
                data: hours,
                splitArea: {
                    show: true
                }
            },
            yAxis: {
                type: 'category',
                data: days,
                splitArea: {
                    show: true
                }
            },
            visualMap: {
                min: 0,
                max: 30, // Set max based on your data range
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: '25%'
            },
            series: [
                {
                    name: 'Punch Card',
                    type: 'heatmap',
                    data: mappedData,
                    label: {
                        show: true
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowColor: 'rgba(0, 0, 0, 0.5)'
                        }
                    }
                }
            ]
        };

        const chartInstance = echarts.getInstanceByDom(chartVisitorsDistroRef.current);
        if (chartInstance) chartInstance.dispose();
        const myChart3 = echarts.init(chartVisitorsDistroRef.current);
        myChart3.setOption(option3);

        return () => {
            myChart3.dispose();
        };
    }


    function renderVisitorsByGender(analyticsData) {
        const { maleData, femaleData, unknownData } = mapGenderDataToChartData(analyticsData);

        const option1 = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'  // Improves readability in stacked bar charts
                },
                formatter: function (params) {
                    let tooltipContent = `<b>${params[0].name}</b><br>`;
                    params.forEach(item => {
                        tooltipContent += `${item.marker} ${item.seriesName}: ${item.value}<br>`;
                    });
                    return tooltipContent;
                }
            },
            xAxis: {
                type: 'category',
                data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            },
            yAxis: {
                type: 'value',
            },
            series: [
                {
                    data: femaleData,
                    type: 'bar',
                    stack: 'visitors',
                    name: 'Female',
                    // itemStyle: { color: '#40aa45' }, // Pink for females
                },
                {
                    data: maleData,
                    type: 'bar',
                    stack: 'visitors',
                    name: 'Male',
                    // itemStyle: { color: '#384db6' }, // Green for males
                },
                {
                    data: unknownData,
                    type: 'bar',
                    stack: 'visitors',
                    name: 'Unknown',
                    // itemStyle: { color: '#9E9E9E' }, // Grey for unknown
                }
            ],
        };


        const chartInstance = echarts.getInstanceByDom(chartGenderRef.current);
        if (chartInstance) chartInstance.dispose();
        const myChart1 = echarts.init(chartGenderRef.current);
        myChart1.setOption(option1);

        return () => myChart1.dispose();
    }

    function mapGenderDataToChartData(analyticsData) {
        const daysOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

        const maleData = [];
        const femaleData = [];
        const unknownData = [];

        // Iterate through each day in the expected order
        daysOrder.forEach((dayKey) => {
            const dayData = analyticsData.days[dayKey];
            if (dayData && dayData.gender_distro) {
                maleData.push(dayData.gender_distro.male || 0);
                femaleData.push(dayData.gender_distro.female || 0);
                unknownData.push(dayData.gender_distro.unknown || 0);
            } else {
                // Ensure consistent data length
                maleData.push(0);
                femaleData.push(0);
                unknownData.push(0);
            }
        });

        return { maleData, femaleData, unknownData };
    }

    function renderVisitorsByAge(analyticsData) {
        const { childrenData, youthData, adultsData, seniorData } = mapAgeDataToChartData(analyticsData);

        const option2 = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                },
                formatter: function (params) {
                    let tooltipContent = `<b>${params[0].name}</b><br>`;
                    params.forEach(item => {
                        tooltipContent += `${item.marker} ${item.seriesName}: ${item.value}<br>`;
                    });
                    return tooltipContent;
                }
            },
            xAxis: {
                type: 'category',
                data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            },
            yAxis: {
                type: 'value',
            },
            series: [
                {
                    data: childrenData,
                    type: 'bar',
                    stack: 'age',
                    name: 'Children (1-12)',
                    // itemStyle: { color: '#FFD700' }, // Gold for children
                },
                {
                    data: youthData,
                    type: 'bar',
                    stack: 'age',
                    name: 'Youth (13-24)',
                    // itemStyle: { color: '#4CAF50' }, // Green for youth
                },
                {
                    data: adultsData,
                    type: 'bar',
                    stack: 'age',
                    name: 'Adults (25-64)',
                    // itemStyle: { color: '#2196F3' }, // Blue for adults
                },
                {
                    data: seniorData,
                    type: 'bar',
                    stack: 'age',
                    name: 'Senior (65+)',
                    // itemStyle: { color: '#56d3ad' }, // Grey for seniors
                }
            ],
        };

        const chartInstance = echarts.getInstanceByDom(chartAgeRef.current);
        if (chartInstance) chartInstance.dispose();
        const myChart2 = echarts.init(chartAgeRef.current);
        myChart2.setOption(option2);

        return () => myChart2.dispose();
    }

    function mapAgeDataToChartData(analyticsData) {
        const daysOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

        const childrenData = [];
        const youthData = [];
        const adultsData = [];
        const seniorData = [];

        // Iterate through each day in the expected order
        daysOrder.forEach((dayKey) => {
            const dayData = analyticsData.days[dayKey];
            if (dayData && dayData.age_distro) {
                childrenData.push(dayData.age_distro.children || 0);
                youthData.push(dayData.age_distro.youth || 0);
                adultsData.push(dayData.age_distro.adults || 0);
                seniorData.push(dayData.age_distro.senior || 0);
            } else {
                // Ensure consistent data length
                childrenData.push(0);
                youthData.push(0);
                adultsData.push(0);
                seniorData.push(0);
            }
        });

        return { childrenData, youthData, adultsData, seniorData };
    }


    return (
        <div>
            <h2>Weekly Analytics</h2>
            {/* Week Selector Controls */}
            <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ marginBottom: '20px' }}
            >
                {/* Week Selector */}
                <ButtonGroup variant="outlined" sx={{ bgcolor: 'background.surface' }}>
                    <Button onClick={handlePreviousWeek}>
                        <KeyboardArrowLeftIcon />
                    </Button>

                    <Typography
                        level="title-md"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: '180px',
                            justifyContent: 'center',
                            paddingX: 3,
                            bgcolor: 'background.level1',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 'md'
                        }}
                    >
                        {new Date(currentWeekStart).toLocaleDateString()} -
                        {new Date(currentWeekStart + 6 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                    </Typography>

                    <Button onClick={handleNextWeek}>
                        <KeyboardArrowRightIcon />
                    </Button>
                </ButtonGroup>

                {/* Gender Selector */}
                <Select
                    defaultValue={['male', 'female', 'unknown']}
                    multiple
                    // onChange={handleGenderChange}
                    placeholder="Gender"
                    sx={{
                        minWidth: '13rem',
                        bgcolor: 'background.surface',
                        borderRadius: 'md',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                    slotProps={{
                        listbox: { sx: { width: '100%' } }
                    }}
                >
                    <Option value="male">Male</Option>
                    <Option value="female">Female</Option>
                    <Option value="unknown">Unknown</Option>
                </Select>

                {/* Age Selector */}
                <Select
                    defaultValue={['children', 'youth', 'adults', 'senior']}
                    multiple
                    // onChange={handleAgeChange}
                    placeholder="Age"
                    sx={{
                        minWidth: '13rem',
                        bgcolor: 'background.surface',
                        borderRadius: 'md',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                    slotProps={{
                        listbox: { sx: { width: '100%' } }
                    }}
                >
                    <Option value="children">Children</Option>
                    <Option value="youth">Youth</Option>
                    <Option value="adults">Adults</Option>
                    <Option value="senior">Senior</Option>
                </Select>
            </Stack>

            {/* Chart container */}
            <Grid container spacing={2} sx={{ flex: 1, height: '100%', minHeight: 0 }}>
                {/* Cameras / Sessions Section */}
                <Grid item xs={2} sx={{ height: '100%', minHeight: 0, borderRadius: 'lg' }} />

                <Grid item xs={7} sx={{ height: '100%', minHeight: 0, borderRadius: 'lg' }}>
                    <div style={{ width: '100%', height: '13.5%' }}></div>
                    <Sheet
                        id="recordingsContainer"
                        sx={{
                            height: '85%', // The Sheet will take up 85% of the height of the parent Grid item
                            minHeight: 0,
                            overflowY: 'auto', // Only the charts inside the Sheet will be scrollable
                            borderRadius: 'lg',
                        }}
                    >

                        {/* Visitors counter chart */}
                        <Typography level="h4" sx={{ fontSize: 'xl', mb: 0.5, marginLeft: 2 }}>
                            Overall Visitor Distribution
                        </Typography>
                        <div ref={chartVisitorsDistroRef} style={{ width: '100%', height: '600px' }} />

                        {/* Duration distro chart */}
                        <Typography level="h4" sx={{ fontSize: 'xl', mb: 0.5, marginLeft: 2 }}>
                            Duration Distribution
                        </Typography>
                        <div ref={chartDurationDistroRef} style={{ width: '100%', height: '600px' }} />

                        {/* Gender chart */}
                        <Typography level="h4" sx={{ fontSize: 'xl', mb: 0.5, marginLeft: 2 }}>
                            Distribution by Gender
                        </Typography>
                        <div ref={chartGenderRef} style={{ width: '100%', height: '400px' }} />

                        {/* Age chart */}
                        <Typography level="h4" sx={{ fontSize: 'xl', mb: 0.5, marginLeft: 2 }}>
                            Distribution by Age Groups
                        </Typography>
                        <div ref={chartAgeRef} style={{ width: '100%', height: '400px' }} />
                    </Sheet>
                </Grid>

                {/* Recordings / Sessions List Section */}
                <Grid item xs={2} sx={{ height: '100%', width: '25%', minHeight: 0, padding: 0 }} />
            </Grid>
        </div>
    );
}

export default AnalyticsTab;
